import { useState, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { Menu, X, Code, TrendingUp, Shield, Cpu, BarChart3, Lock, Mail } from "lucide-react";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_portfolio-dev-27/artifacts/tykowhyy_CrowsEarLogo5-hw.png";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Projects", href: "#projects" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <a href="#" data-testid="nav-logo" className="flex-shrink-0">
            <img src={LOGO_URL} alt="Crow's Ear Investments" className="h-20 sm:h-24" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase()}-link`}
                className="text-[#A1A1AA] hover:text-white transition-colors duration-300 text-sm font-medium link-underline"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mobile-menu md:hidden absolute top-20 left-0 right-0 border-t border-white/5"
        >
          <div className="container-custom py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-${link.name.toLowerCase()}-link`}
                className="block text-[#A1A1AA] hover:text-white transition-colors duration-300 text-lg font-medium py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1766958205141-2d775ea560a1?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="hero-overlay absolute inset-0" />
      
      <div className="container-custom relative z-10 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <span className="overline mb-6 block" data-testid="hero-overline">Technology Company</span>
          <h1
            data-testid="hero-heading"
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.1] text-white mb-8"
          >
            Proprietary Software.
            <br />
            <span className="text-[#D4A84B]">Technology</span> Development.
          </h1>
          <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mb-10 leading-relaxed">
            A founder-managed technology company focused on building proprietary software tools and operating secure digital systems.
          </p>
          <a
            href="#about"
            data-testid="hero-cta"
            className="btn-primary inline-block px-8 py-4 text-sm font-semibold tracking-wider uppercase"
          >
            Discover Our Approach
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="section-padding bg-[#0A0A0A]">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="overline mb-4 block">About Us</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white">
              A Technology-Focused Company
            </h2>
            <div className="accent-line mt-6" />
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-7 space-y-6"
          >
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              Crow's Ear Investments LLC is a privately held limited liability company focused primarily on <span className="text-white font-medium">software development</span> and <span className="text-white font-medium">technology solutions</span>.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              The company designs and maintains proprietary software systems used for data analysis, workflow automation, digital infrastructure, and operational efficiency tools. These systems may be used internally or made available to clients as part of broader technology solutions.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              Operating as a small, founder-managed entity, we place emphasis on <span className="text-white font-medium">responsible governance</span>, <span className="text-white font-medium">secure technology practices</span>, and <span className="text-white font-medium">compliant operations</span>.
            </p>

            {/* Key Values */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 text-[#D4A84B]" />
                <span className="text-white text-sm font-medium">Secure Systems</span>
              </div>
              <div className="flex items-center space-x-3">
                <Lock className="w-5 h-5 text-[#D4A84B]" />
                <span className="text-white text-sm font-medium">Compliant Operations</span>
              </div>
              <div className="flex items-center space-x-3">
                <Cpu className="w-5 h-5 text-[#D4A84B]" />
                <span className="text-white text-sm font-medium">Innovation-Driven</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="section-padding bg-[#050505]">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline mb-4 block">Our Services</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Software Development
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Software Development Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="service-software-card"
            className="md:col-span-7 bento-card p-8 lg:p-12"
          >
            <div className="mb-6">
              <div className="w-12 h-12 bg-[#D4A84B]/10 flex items-center justify-center mb-6">
                <Code className="w-6 h-6 text-[#D4A84B]" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight mb-4">
                Software Development
              </h3>
              <p className="text-[#A1A1AA] text-base leading-relaxed mb-6">
                We design and build custom software tools intended to improve operational efficiency and digital workflows.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Cpu className="w-5 h-5 text-[#2CBFB1] mt-0.5" />
                <div>
                  <span className="text-white text-sm font-medium">Data Analysis Tools</span>
                  <p className="text-[#A1A1AA] text-sm">Software that helps organize and analyze operational data</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <BarChart3 className="w-5 h-5 text-[#2CBFB1] mt-0.5" />
                <div>
                  <span className="text-white text-sm font-medium">Workflow Automation</span>
                  <p className="text-[#A1A1AA] text-sm">Systems that automate repetitive digital tasks</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-[#2CBFB1] mt-0.5" />
                <div>
                  <span className="text-white text-sm font-medium">Digital Infrastructure</span>
                  <p className="text-[#A1A1AA] text-sm">Secure backend systems designed for reliability and scalability</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Company Operations Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="service-operations-card"
            className="md:col-span-5 bento-card p-8 lg:p-12"
          >
            <div className="mb-6">
              <div className="w-12 h-12 bg-[#D4A84B]/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-[#D4A84B]" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight mb-4">
                Company Operations
              </h3>
              <p className="text-[#A1A1AA] text-base leading-relaxed mb-6">
                Crow's Ear Investments LLC operates as a privately owned company managing its own internal business operations.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Lock className="w-5 h-5 text-[#2CBFB1] mt-0.5" />
                <div>
                  <span className="text-white text-sm font-medium">Internal Operations</span>
                  <p className="text-[#A1A1AA] text-sm">Managing company resources and growth</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-[#2CBFB1] mt-0.5" />
                <div>
                  <span className="text-white text-sm font-medium">Compliant Practices</span>
                  <p className="text-[#A1A1AA] text-sm">Responsible governance standards</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-xs text-[#A1A1AA]">
                We do not provide investment services, brokerage services, or financial advisory services to the public.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    {
      name: "LingoFlow",
      description: "AI-driven language practice application focusing on real-time conversational fluency for expats and professionals.",
      url: "https://speak-es-en.preview.emergentagent.com/home",
      status: "Beta",
      tags: ["AI", "EdTech", "Language Learning"],
      image: "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
      name: "Metro Real Estate Investments",
      description: "A refined digital platform for Atlanta-based real estate investment, acquisitions, and strategic asset management.",
      url: "https://app-forge-1982.preview.emergentagent.com/",
      status: "Live",
      tags: ["Real Estate", "Fintech", "Consulting"],
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
      name: "VisaReady",
      description: "Immigration preparation and document management platform helping applicants navigate the visa process with confidence.",
      url: "https://visaready.net",
      status: "Beta",
      tags: ["SaaS", "Immigration", "Document Management"],
      image: "https://images.unsplash.com/photo-1569098644584-210bcd375b59?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
  ];

  return (
    <section id="projects" data-testid="projects-section" className="section-padding bg-[#0A0A0A]">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline mb-4 block">Our Work</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Projects & Portfolio
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              data-testid="project-card"
              className="bento-card overflow-hidden flex flex-col"
            >
              {/* Project Screenshot */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={`${project.name} preview`}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
                {/* Status Badge Overlay */}
                <div className="absolute top-4 left-4">
                  <span className={`inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase border ${
                    project.status === 'Live' 
                      ? 'bg-[#2CBFB1]/20 text-[#2CBFB1] border-[#2CBFB1]/30' 
                      : 'bg-[#D4A84B]/20 text-[#D4A84B] border-[#D4A84B]/30'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>

              <div className="p-6 flex flex-col flex-grow">
                {/* Project Name */}
                <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight mb-3">
                  {project.name}
                </h3>

                {/* Description */}
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4 flex-grow">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs text-[#A1A1AA] px-2 py-1 border border-white/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Link */}
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`project-link-${project.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="inline-flex items-center text-sm font-medium text-white hover:text-[#D4A84B] transition-colors group"
                >
                  Visit Site
                  <svg
                    className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="section-padding bg-[#050505]">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="overline mb-4 block">Get In Touch</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-8">
            Let's Build Together
          </h2>
          
          <div className="flex items-center justify-center space-x-3 mb-8">
            <Mail className="w-6 h-6 text-[#D4A84B]" />
            <a
              href="mailto:info@crowsearinvestments.com"
              data-testid="contact-email-link"
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white email-link tracking-tight"
            >
              info@crowsearinvestments.com
            </a>
          </div>

          <p className="text-[#A1A1AA] text-base">
            Have questions about our software solutions or want to learn more about what we do? Reach out to us directly.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#050505] border-t border-white/5 pt-16 pb-8">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Crow's Ear Investments" className="h-16 sm:h-20 mb-6" />
            <p className="text-[#A1A1AA] text-sm max-w-md leading-relaxed">
              A privately held technology company focused on proprietary software development and secure digital systems. Founded with an emphasis on innovation and compliant operations.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 tracking-wider uppercase">Navigation</h4>
            <ul className="space-y-3">
              <li><a href="#about" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">About</a></li>
              <li><a href="#services" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">Services</a></li>
              <li><a href="#projects" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">Projects</a></li>
              <li><a href="#contact" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 tracking-wider uppercase">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:info@crowsearinvestments.com" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">
                  info@crowsearinvestments.com
                </a>
              </li>
              <li>
                <a href="https://crowsearinvestments.com" className="text-[#A1A1AA] text-sm hover:text-white transition-colors">
                  crowsearinvestments.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Compliance Notice */}
        <div className="mb-12 p-6 border border-white/10 bg-[#0A0A0A]">
          <h4 className="text-white text-sm font-semibold mb-3 tracking-wider uppercase">Compliance Notice</h4>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">
            Crow's Ear Investments LLC is a technology company. We do not provide investment services, brokerage services, or financial advisory services to the public. Payments made through this website are strictly for software services, technology solutions, or related digital products.
          </p>
        </div>

        {/* Giant Footer Text */}
        <div className="overflow-hidden mb-12">
          <span className="footer-giant-text block text-center whitespace-nowrap">
            CROW'S EAR
          </span>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/5 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-[#A1A1AA] text-xs">
              &copy; {new Date().getFullYear()} Crow's Ear Investments LLC. All rights reserved.
            </p>
            <p className="text-[#A1A1AA] text-xs">
              A founder-managed technology company
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App" data-testid="app-root">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ProjectsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
